"use client";

import Link from "next/link";
import { motion } from "framer-motion";
import { useEffect, useState } from "react";

export default function NotFound() {
  const [isClient, setIsClient] = useState(false);

  useEffect(() => {
    setIsClient(true);
  }, []);

  // Sprinkle animation variants
  const sprinkleVariants = {
    initial: { scale: 0, rotate: 0 },
    animate: (i: number) => ({
      scale: 1,
      rotate: 360,
      transition: {
        delay: i * 0.1,
        duration: 0.5,
        repeat: Infinity,
        repeatType: "reverse" as const,
      },
    }),
  };

  // Text animation variants
  const textVariants = {
    initial: { opacity: 0, y: 20 },
    animate: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        ease: "easeOut",
      },
    },
  };

  // Generate random positions for sprinkles
  const sprinkles = Array.from({ length: 20 }, (_, i) => ({
    id: i,
    top: `${Math.random() * 100}%`,
    left: `${Math.random() * 100}%`,
    color: ['#FF8080', '#e1cf1d', '#037a6a', '#FFC0CB'][Math.floor(Math.random() * 4)],
  }));

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#FFF5F4] to-white flex flex-col items-center justify-center relative overflow-hidden px-4">
      {/* Animated sprinkles (only rendered client-side after mount) */}
      {isClient &&
        sprinkles.map((sprinkle) => (
          <motion.div
            key={sprinkle.id}
            className="absolute w-3 h-3 rounded-full"
            style={{
              top: sprinkle.top,
              left: sprinkle.left,
              backgroundColor: sprinkle.color,
            }}
            variants={sprinkleVariants}
            initial="initial"
            animate="animate"
            custom={sprinkle.id}
          />
        ))}

      {/* Main content */}
      <motion.div
        className="text-center z-10"
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        <motion.div
          className="text-9xl font-bold mb-4 bg-clip-text text-transparent bg-gradient-to-r from-[#FF8080] to-[#037a6a]"
          initial={{ y: -50 }}
          animate={{ y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          404
        </motion.div>

        <motion.h1
          className="text-4xl md:text-5xl font-bold mb-6 text-gray-800"
          variants={textVariants}
          initial="initial"
          animate="animate"
        >
          Something Sweet is Coming Soon!
        </motion.h1>

        <motion.p
          className="text-lg md:text-xl text-gray-600 mb-8"
          variants={textVariants}
          initial="initial"
          animate="animate"
        >
          We&apos;re baking up something special for this page.
        </motion.p>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
        >
          <Link
            href="/"
            className="inline-flex items-center justify-center bg-[#037a6a] text-white px-6 py-3 rounded-full font-bold hover:bg-[#025a4f] transition-colors duration-300"
          >
            <span className="mr-2">🏠</span>
            Back to Home
          </Link>
        </motion.div>
      </motion.div>

      {/* Animated cake illustration */}
      <motion.div
        className="absolute bottom-0 w-full h-32 bg-[#037a6a]/10"
        initial={{ y: 100 }}
        animate={{ y: 0 }}
        transition={{
          duration: 1,
          ease: "easeOut",
          delay: 0.5,
        }}
      >
        <motion.div
          className="w-full h-full"
          animate={{
            y: [0, -10, 0],
          }}
          transition={{
            duration: 2,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        >
          <svg
            viewBox="0 0 1440 320"
            className="absolute bottom-0 w-full"
            preserveAspectRatio="none"
          >
            <path
              fill="#037a6a"
              fillOpacity="0.1"
              d="M0,96L48,112C96,128,192,160,288,160C384,160,480,128,576,112C672,96,768,96,864,112C960,128,1056,160,1152,160C1248,160,1344,128,1392,112L1440,96L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z"
            ></path>
          </svg>
        </motion.div>
      </motion.div>
    </div>
  );
}

